import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  Alert,
  AlertTitle,
  CircularProgress,
  Fab,
  Grid,
  IconButton,
  Paper,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Toolbar,
  Tooltip,
} from "@mui/material";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import { memo, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ApiFetch from "../../components/ApiFetch";
import InputControl from "../../components/controls/InputControl";
import SelectControl from "../../components/controls/SelectControl";
import { API_QUOTES } from "../../context/ApiEndPoints";
import useAppContext from "../../hooks/useAppContext";
import useComponentContext from "../../hooks/useComponentContext";
import useFetchList from "../../hooks/useFetchList";
import useTable from "../../hooks/useTable";
import { currencyFormat } from "../../scripts/Scripts";
dayjs.extend(localizedFormat);

const columns = [
  { field: "quoteId", label: "ID", width: 1 },
  { field: "industry", label: "Industry / Title", width: 1 },
  {
    field: "stage",
    label: "Job Status",
    width: 2,
    filters: [
      { value: "", label: "All" },
      { value: "1", label: "Job" },
      { value: "2", label: "Design" },
      { value: "3", label: "Installation" },
    ],
  },
  {
    field: "invoiceStatus",
    label: "Financial Status",
    width: 2,
    filters: [
      { value: "", label: "All" },
      { value: "request", label: "Request" },
      { value: "sent", label: "Sent" },
      { value: "approved", label: "Approved" },
      { value: "billable", label: "Billable" },
      { value: "invoiced", label: "Invoiced" },
      { value: "paid", label: "Paid" },
      { value: "waiver", label: "Waiver" },
      { value: "lost", label: "Lost" },
    ],
  },
  { field: "totalPrice", label: "Total Price", width: 1 },
  { field: "customer", label: "Customer", width: 1 },
  { field: "user", label: "User", width: 1 },
  { field: "createdDate", label: "Created On", width: 1 },
  { field: "modifiedDate", label: "Modified On", width: 1 },
  {
    field: "action",
    label: "Actions",
    width: 1,
    disableSorting: true,
    textCenter: true,
    isAction: true,
  },
];

const stageStr = (int) => {
  switch (int) {
    case 1:
      return "Job";
    case 2:
      return "Design";
    case 3:
      return "Installation";
    case 4:
      return "Invoice";
    default:
      return "Job";
  }
};

const statusStr = (int) => {
  switch (int) {
    case "Sent":
    case "Request":
    case "Approved":
    case "Billable":
    case "Lost":
      return "Quote";

    case "Invoiced":
    case "Paid":
    case "Waiver":
      return "Invoice";

    default:
      return "Quote";
  }
};

const alertStatus = (status) => {
  switch (status) {
    case "Request":
    case "Todo":
    case "Billable":
      return "warning";

    case "Sent":
    case "Waiting":
    case "Invoiced":
      return "info";

    case "Approved":
    case "Completed":
    case "Paid":
      return "success";

    case "Booked":
    case "Lost":
    case "Waiver":
      return "error";

    default:
      return "info";
  }
};

function JobsList() {
  const { user } = useAppContext();
  const { setNotify, setConfirmDialog, scrhObjs, setScrhObjs } =
    useComponentContext();
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const { records, setRecords, isLoading, fetchError } =
    useFetchList(API_QUOTES);

  const [filterFn, setFilterFn] = useState({
    fn: (items) => {
      return items;
    },
  });

  const { TblHead, TblContainer, TblPagination, recordsAfterPagingAndSorting } =
    useTable(records, columns, filterFn);

  const handleSearch = (e) => {
    const { name, value } = e.target;
    const index = scrhObjs.findIndex((el) => el.name === name);
    if (index >= 0) {
      scrhObjs[index] = { name, value };
    } else {
      scrhObjs.push({ name, value });
    }

    setScrhObjs((scrhObjs) => scrhObjs.filter((el) => el.value !== ""));
  };

  useEffect(() => {
    let timeOut = setTimeout(() => {
      setFilterFn({
        fn: (items) => {
          let x;
          for (const { name, value } of scrhObjs) {
            x = items.filter((item) =>
              item[name]?.toString().toLowerCase().includes(value),
            );

            items = x;
          }
          return items;
        },
      });
    }, 300);

    return () => clearTimeout(timeOut);
    // eslint-disable-next-line
  }, [scrhObjs]);

  const handleDelete = async (quoteId) => {
    setConfirmDialog({ isOpen: false });

    // DELETE Request using fetch
    const requestOptions = {
      method: "DELETE",
      headers: {
        Authorization: "Bearer " + user.token,
        "Content-Type": "application/json",
      },
    };
    const results = await ApiFetch(
      `${API_QUOTES}/${quoteId}`,
      requestOptions,
      setNotify,
      "Deleted Successfully!",
    );

    if (results === "DELETE") {
      setSelectedIndex(-1);
      setRecords((records) => records.filter((el) => el.quoteId !== quoteId));
    }
  };

  return (
    <>
      {fetchError ||
        (isLoading && (
          <CircularProgress size={20} color="inherit" sx={{ m: 2 }} />
        ))}
      {records && (
        <>
          <Toolbar component={Paper} square disableGutters sx={{ mb: 0.1 }}>
            <Grid container alignItems="center">
              {columns.map((column) =>
                !column.isAction ? (
                  <Grid item xs={column.width} key={column.field} sx={{ p: 2 }}>
                    {!column.filters ? (
                      <InputControl
                        variant="standard"
                        label={`Search ${column.label}`}
                        name={column.field}
                        value={
                          scrhObjs.find((el) => el.name === column.field)
                            ?.value || ""
                        }
                        // InputProps={{
                        //   startAdornment: (
                        //     <InputAdornment position="start">
                        //       <SearchIcon />
                        //     </InputAdornment>
                        //   ),
                        // }}
                        onChange={(e) => handleSearch(e)}
                        className="srchInput"
                        margin="none"
                        fullWidth={false}
                      />
                    ) : (
                      <SelectControl
                        select
                        variant="standard"
                        label={`Search ${column.label}`}
                        name={column.field}
                        value={
                          scrhObjs.find((el) => el.name === column.field)
                            ?.value || []
                        }
                        // InputProps={{
                        //   startAdornment: (
                        //     <InputAdornment position="start">
                        //       <SearchIcon />
                        //     </InputAdornment>
                        //   ),
                        // }}
                        onChange={(e) => handleSearch(e)}
                        options={column.filters}
                        className="srchInput"
                        margin="none"
                        fullWidth={true}
                      />
                    )}
                  </Grid>
                ) : (
                  <Grid
                    item
                    xs={column.width}
                    key={column.field}
                    sx={{ p: 2 }}
                    textAlign="right"
                  >
                    {user?.claims.includes("job_add") && (
                      <Tooltip title="Add New" placement="top" arrow>
                        <Fab
                          color="primary"
                          aria-label="add"
                          component={Link}
                          to="/add-quote"
                        >
                          <AddIcon />
                        </Fab>
                      </Tooltip>
                    )}
                  </Grid>
                ),
              )}
            </Grid>
          </Toolbar>
          <Paper sx={{ width: "100%", overflow: "hidden" }}>
            <TableContainer sx={{ maxHeight: "69vh" }}>
              <TblContainer>
                {records.length === 0 && <caption>No Record Found!</caption>}
                <TblHead />
                <TableBody>
                  {records &&
                    recordsAfterPagingAndSorting().map((record, index) => (
                      <TableRow
                        key={record.quoteId}
                        selected={selectedIndex === index}
                        onClick={() => setSelectedIndex(index)}
                        hover
                      >
                        <TableCell
                          sx={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {record.quoteId}
                        </TableCell>
                        <TableCell
                          sx={{
                            whiteSpace: "pre-line",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {record.industry}
                        </TableCell>
                        <TableCell
                          sx={{
                            whiteSpace: "pre-line",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          <Alert
                            variant="outlined"
                            severity={alertStatus(record.status)}
                          >
                            <AlertTitle>{stageStr(record.stage)}</AlertTitle>
                            {record.status}
                          </Alert>
                        </TableCell>
                        <TableCell
                          sx={{
                            whiteSpace: "pre-line",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          <Alert severity={alertStatus(record.invoiceStatus)}>
                            <AlertTitle>
                              {statusStr(record.invoiceStatus)}
                            </AlertTitle>
                            {record.invoiceStatus}
                          </Alert>
                        </TableCell>
                        <TableCell
                          sx={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {currencyFormat(record.totalPrice, record.currency)}
                        </TableCell>
                        <TableCell
                          sx={{
                            whiteSpace: "pre-line",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {record.customer}
                        </TableCell>
                        <TableCell
                          sx={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {record.user}
                        </TableCell>
                        <TableCell
                          sx={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {record.createdDate}
                        </TableCell>
                        <TableCell
                          sx={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {record.modifiedDate}
                        </TableCell>
                        <TableCell
                          sx={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            textAlign: "center",
                          }}
                        >
                          <Tooltip title="Edit" placement="top" arrow>
                            <IconButton
                              aria-label="edit"
                              color="primary"
                              component={Link}
                              to={`/job-details/${record.quoteId}`}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          {user && user.claims.includes("job_delete") && (
                            <Tooltip title="Delete" placement="top" arrow>
                              <IconButton
                                color="error"
                                aria-label="delete"
                                onClick={() =>
                                  setConfirmDialog({
                                    isOpen: true,
                                    title: `ID: ${record.quoteId} \n Are you sure to delete this record?`,
                                    subTitle: "You can't undo this operation",
                                    onConfirm: () => {
                                      handleDelete(record.quoteId);
                                    },
                                  })
                                }
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </TblContainer>
            </TableContainer>
            <TblPagination />
          </Paper>
        </>
      )}
    </>
  );
}

export default memo(JobsList);
